import { memo, useState } from "react";
import { openConfirmModal } from "@mantine/modals";

import {
  ActionIcon,
  Drawer,
  Group,
  Image,
  Stack,
  Text,
  Tooltip
} from "@mantine/core";
import { TbRefresh } from "react-icons/tb";

import ChatThread from "../ChatThread";
import LoadingSpinner from "modules/core/components/LoadingSpinner";

import { useDeleteChatThreadMutation } from "modules/chat/hooks/mutations/useDeleteChatThreadMutation";
import { useThreadMessagesQuery } from "modules/chat/hooks/queries/useThreadMessagesQuery";
import { useChatThread } from "../ChatThread/useChatThread";

import { MosaicChatAgent } from "modules/chat/types/entities";

import mosaicLogo from "modules/core/assets/img/mosaic.png";

import "./styles.css";

type ChatDrawerProps = {
  threadId: string;
  agentId: MosaicChatAgent;
  initialProjectIds: string[];
  title?: string;
  emptyView?: {
    title: string;
    description: string;
    context?: string[];
    suggestedMessages?: string[];
  };
  initialContext?: Record<string, unknown>;
  onClose: () => void;
};

const chatTitles: Record<MosaicChatAgent, string> = {
  "brainstorm-synthesis": "Synthesize a Brainstorm Idea",
  mosaic: "Ask Mosaic"
};

const ChatDrawer = (props: ChatDrawerProps) => {
  const {
    threadId,
    agentId,
    initialProjectIds,
    title,
    emptyView,
    initialContext,
    onClose
  } = props;

  const [anchorDate, setAnchorDate] = useState(new Date());

  const { messages, status, sendMessage, stop, clear, mergeHistory } =
    useChatThread({
      agentId,
      threadId,
      initialProjectIds,
      initialContext
    });

  const {
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError,
    isLoading,
    remove: removeThreadMessages
  } = useThreadMessagesQuery(
    {
      before: anchorDate,
      threadId
    },
    {
      onSuccess: data => {
        mergeHistory(data.pages.flatMap(page => page.messages));
      }
    }
  );

  const { mutate: deleteThread, isLoading: isDeletingThread } =
    useDeleteChatThreadMutation();

  return (
    <Drawer
      opened
      size="lg"
      padding={0}
      position="right"
      withCloseButton={false}
      closeOnEscape={false}
      title={
        <Group gap="xs" w="100%" justify="space-between">
          <Group gap="md">
            <Image src={mosaicLogo} alt="Mosaic" w={20} h={20} />

            <Text
              fw={700}
              fz="sm"
              lineClamp={1}
              title={title || chatTitles[agentId]}
            >
              {title || chatTitles[agentId]}
            </Text>
          </Group>

          {messages.length > 0 ? (
            <Tooltip label="Clear Conversation">
              <ActionIcon
                variant="light"
                color="red"
                size="sm"
                loading={isDeletingThread}
                onClick={() => {
                  openConfirmModal({
                    title: "Clear Conversation",
                    children: (
                      <Text fz="sm" c="gray.7">
                        Are you sure you want to clear the conversation?
                      </Text>
                    ),
                    labels: {
                      confirm: "Clear Conversation",
                      cancel: "Cancel"
                    },
                    confirmProps: {
                      color: "red"
                    },
                    onConfirm: () => {
                      deleteThread(
                        {
                          threadId,
                          agentId
                        },
                        {
                          onSuccess: () => {
                            clear();
                            setAnchorDate(new Date());
                            removeThreadMessages();
                          }
                        }
                      );
                    }
                  });
                }}
              >
                <TbRefresh />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      }
      onClose={onClose}
      styles={{
        body: {
          height: "calc(100% - 70px)"
        },
        header: {
          padding: "16px 24px",
          height: 70,
          backgroundColor: "var(--mantine-color-gray-9)",
          color: "var(--mantine-color-white)"
        },
        title: {
          width: "100%"
        }
      }}
    >
      {isLoading ? (
        <LoadingSpinner fullWidth label="Loading conversation..." />
      ) : (
        <Stack gap={0} h="100%">
          <ChatThread
            status={status}
            messages={messages}
            sendMessage={sendMessage}
            stopChat={stop}
            emptyView={emptyView}
            hasNextPage={!!hasNextPage}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </Stack>
      )}
    </Drawer>
  );
};

export default memo(ChatDrawer);
